import {
  Col,
  DatePicker,
  Form,
  Layout,
  message,
  Row,
  Tabs,
  Table,
} from "antd";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatDate,
  formatNumber,
  formatString,
  getDateRange,
  navigateTo,
} from "../../Utils/Common";
import { postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useTableData } from "../../Utils/useTableData";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const CreditBillingPayment = () => {
  const [loading, setLoading] = useState("");
  const [showModal, setShowModal] = useState("");
  const dateRange = getDateRange("month");
  const [form] = Form.useForm();
  const [selectedRow, setSelectedRow]: any = useState({});
  const [activeTab, setActiveTab] = useState("pending");

  const {
    dataSource,
    dataSize,
    loading: tableLoading,
    filterConfig,
    setFilterConfig,
    refetch: fetchTableData,
    handleChangePage,
    handlePageSizeChange,
  } = useTableData<any>({
    endpoint: "credit_billing_payments/get",
    initialFilters: {
      date_from: formatDate(dateRange[0], "YYYY-MM-DD"),
      date_to: formatDate(dateRange[1], "YYYY-MM-DD"),
      status: "approved",
    },
    isFormData: true,
  });

  function clearFields() {
    setSelectedRow({});
    form.resetFields();
  }

  function onChangeTab(activeKey: any) {
    setActiveTab(activeKey);
    setFilterConfig((prev: any) => ({
      ...prev,
      status: activeKey,
    }));
  }

  function getTableColumns() {
    switch (activeTab) {
      case "pending":
        return [
          {
            title: "PR No.",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Transaction Date",
            dataIndex: "transaction_date",
            key: "transaction_date",
            render: (text: string) => dayjs(text).format("MMMM D, YYYY"), // ← format here
          },
          {
            title: "Supplier Name",
            dataIndex: "supplier_name",
            key: "supplier_name",
          },
          {
            title: "Created By",
            dataIndex: "added_by_name",
            key: "added_by_name",
          },
        ];
      case "approved":
        return [
          {
            title: "PR No.",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Transaction Date",
            dataIndex: "transaction_date",
            key: "transaction_date",
            render: (text: string) => dayjs(text).format("MMMM D, YYYY"), // ← format here
          },
          {
            title: "Supplier Name",
            dataIndex: "supplier_name",
            key: "supplier_name",
          },
          {
            title: "Approved By",
            dataIndex: "approved_by_name",
            key: "approved_by_name",
          },
        ];
      case "disapproved":
        return [
          {
            title: "PR No.",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Transaction Date",
            dataIndex: "transaction_date",
            key: "transaction_date",
            render: (text: string) => dayjs(text).format("MMMM D, YYYY"), // ← format here
          },
          {
            title: "Supplier Name",
            dataIndex: "supplier_name",
            key: "supplier_name",
          },
          {
            title: "Disapproved By",
            dataIndex: "disapproved_by_name",
            key: "disapproved_by_name",
          },
        ];
      default:
        return [];
    }
  }

  function handleFilterChange(value: any, field: string) {
    if (field === "dates") {
      if (!value || value.length !== 2) {
        // Clear the date filters
        setFilterConfig((prev: any) => ({
          ...prev,
          date_from: null,
          date_to: null,
        }));
        return;
      }
      setFilterConfig((prev: any) => ({
        ...prev,
        date_from: value[0]?.format("YYYY-MM-DD"), // or "YYYY-MM" if you prefer
        date_to: value[1]?.format("YYYY-MM-DD"),
      }));
    } else {
      setFilterConfig((prev: any) => ({
        ...prev,
        [field]: value,
      }));
    }
  }

  const columns = [
    {
      title: "Billing No.",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Company Name",
      dataIndex: "company_name",
      key: "company_name",
    },
    {
      title: "Amount",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "right" as any,
      render: (value: any) => formatNumber(value),
    },
    {
      title: "Added by",
      dataIndex: "added_by_name",
      key: "added_by_name",
    },
    {
      title: "Added on",
      dataIndex: "added_on",
      key: "added_on",
      render: (text: string) => dayjs(text).format("MMMM D, YYYY h:mm A"),
    },
  ];

  async function submitForm(data: any) {
    setLoading("submit");
    var response = await postRequest(
      `purchase_requests/${showModal}`,
      { purchase_request_id: selectedRow.id, ...data },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        fetchTableData();
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${showModal} purchase request.`
      );
    }
  }

  async function deleteExpense() {
    var response = await postRequest(
      `purchase_requests/delete`,
      {
        purchase_request_id: selectedRow.id,
      },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        setShowModal("");
        clearFields();
        fetchTableData();
      }, 1000);
    } else {
      message.error(
        formatString(
          response?.data?.response?.data?.messages?.error_messages[0] ??
            "Failed to delete purchase request."
        )
      );
    }
  }

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Credit Billing Payment"
              withDateRangeFilter={false}
              withAddButton={true}
              addBtnText="Credit Billing Payment"
              onCreate={() => navigateTo("/credit-billing-payment/add")}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({
                  ...prev,
                  search: value,
                }))
              }
            />
          </Col>
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="custom-table min-w-[800px]">
                <Table
                  bordered
                  dataSource={dataSource}
                  columns={columns}
                  rowKey="id"
                  loading={tableLoading}
                  size="small"
                  pagination={{
                    current: filterConfig.page,
                    total: dataSize,
                    showSizeChanger: true,
                    onChange: (page: number) => handleChangePage(page),
                    onShowSizeChange: (_: number, size: number) => handlePageSizeChange(size),
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Layout>
      <PersistentFooter />
    </Layout>
  );
};

export default CreditBillingPayment;
